import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { apiClient, ContactFormData } from '../../lib/api'

const contactMethods = [
  {
    name: 'Email Us',
    description: 'Questions, feedback, or just want to chat? We read everything personally',
    icon: EnvelopeIcon,
    contact: 'support@reddminer.com',
    responseTime: 'Usually within a few hours',
    available: 'We check email constantly 📧'
  },
  {
    name: 'In-App Chat',
    description: 'Got an account? Use the built-in chat for quick help while you work',
    icon: ChatBubbleLeftRightIcon,
    contact: 'Available after login',
    responseTime: 'Pretty fast!',
    available: 'Look for the chat icon in your dashboard'
  }
]

const faqs = [
  {
    question: 'How quickly can I start finding leads?',
    answer: 'Most customers start finding qualified leads within 24 hours of setup. Our AI begins monitoring immediately and gets smarter over time.'
  },
  {
    question: 'Is there a setup fee or long-term contract?',
    answer: 'No setup fees, no long-term contracts. You can start with our 14-day free trial and cancel anytime.'
  },
  {
    question: 'How does Reddit compliance work?',
    answer: 'Our platform is designed to follow Reddit\'s terms of service. We use advanced algorithms to ensure natural, helpful engagement that adds value to communities.'
  },
  {
    question: 'Can you help with custom implementation?',
    answer: 'Yes! Our Enterprise plan includes dedicated account management and custom implementation support.'
  },
  {
    question: 'Do you offer training or onboarding?',
    answer: 'We provide comprehensive onboarding for all customers, plus video tutorials and documentation. Enterprise customers get dedicated training sessions.'
  },
  {
    question: 'What if I need a feature that doesn\'t exist?',
    answer: 'We love feedback! Many of our features come from customer requests. Contact us to discuss custom development options.'
  }
]

const ContactPage = () => {
  const location = useLocation()

  // Get inquiry type from URL parameter (e.g., ?type=support)
  const getInitialInquiryType = () => {
    const urlParams = new URLSearchParams(location.search)
    const typeParam = urlParams.get('type')

    // Map URL parameter to valid inquiry type
    const typeMapping: Record<string, string> = {
      'support': 'support',
      'technical': 'support',
      'sales': 'sales',
      'general': 'general',
      'partnership': 'partnership',
      'feature': 'feature'
    }

    return typeMapping[typeParam || ''] || 'general'
  }

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    message: '',
    inquiryType: getInitialInquiryType()
  })
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Spam protection state
  const [formTimestamp, setFormTimestamp] = useState<number>(0)
  const [honeypotFields, setHoneypotFields] = useState({
    website: '',
    url: '',
    phone_number: '',
    fax: ''
  })

  // Form section reference for scrolling
  const formSectionRef = useRef<HTMLDivElement>(null)

  // Initialize form timestamp when component mounts
  useEffect(() => {
    setFormTimestamp(Math.floor(Date.now() / 1000))
  }, [])

  // Update inquiry type if URL parameters change
  useEffect(() => {
    const newInquiryType = getInitialInquiryType()
    setFormData(prev => ({ ...prev, inquiryType: newInquiryType }))
  }, [location.search])

  // Scroll to form section smoothly
  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)
    setSuccessMessage(null)

    try {
      console.log('📧 Submitting contact form...', formData)

      // Enhanced form data with spam protection
      const enhancedFormData = {
        ...formData,
        form_timestamp: formTimestamp,
        // Honeypot fields (should remain empty)
        website: honeypotFields.website,
        url: honeypotFields.url,
        phone_number: honeypotFields.phone_number,
        fax: honeypotFields.fax
      }

      const response = await apiClient.sendContactForm(enhancedFormData)

      console.log('✅ Contact form response:', response)

      if (response.success) {
        setIsSubmitted(true)
        setSuccessMessage(response.message)

        // Clear form data
        setFormData({
          name: '',
          email: '',
          company: '',
          message: '',
          inquiryType: 'general'
        })

        // Reset spam protection fields
        setFormTimestamp(Math.floor(Date.now() / 1000))
        setHoneypotFields({
          website: '',
          url: '',
          phone_number: '',
          fax: ''
        })

        // Scroll to form section to show success message
        setTimeout(() => {
          scrollToForm()
        }, 100)
      } else {
        setError('Failed to send message. Please try again.')
      }
    } catch (err: any) {
      console.error('❌ Contact form error:', err)

      // Handle different types of errors
      if (err.message && err.message.includes('rate limit')) {
        setError('Too many messages sent. Please try again in an hour.')
      } else if (err.message && err.message.includes('email')) {
        setError('Please enter a valid email address.')
      } else {
        setError(err.message || 'Failed to send message. Please try again or contact us directly at support@reddminer.com.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-4xl py-24 sm:py-32">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
            >
              Let's Chat!
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                I'm Just an Email Away
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto"
            >
              Got questions? Want to share feedback? Just say hi?
              We're a small team and we personally read and respond to every message.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Contact Methods */}
      <div className="py-24 sm:py-32 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              How to Reach Me
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No call centers, no ticket systems - just our small team, genuinely excited to help
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {contactMethods.map((method, index) => (
              <motion.div
                key={method.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-8 shadow-lg text-center"
              >
                <method.icon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{method.name}</h3>
                <p className="text-gray-600 mb-4">{method.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="font-medium text-blue-600">{method.contact}</div>
                  <div className="text-gray-500">
                    <ClockIcon className="h-4 w-4 inline mr-1" />
                    {method.responseTime}
                  </div>
                  <div className="text-gray-500">Available: {method.available}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              ref={formSectionRef}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <p className="text-lg text-gray-600 mb-8">
                Fill out the form below and our team will get back to you quickly.
                We usually respond within a few hours (sometimes faster!).
              </p>

              <div className="space-y-4">
                <div className="flex items-center text-gray-700">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Real humans, not bots</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>Usually respond within hours</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>We love feedback and ideas</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                  <span>No sales pitch, promise!</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              {isSubmitted ? (
                <div className="bg-green-50 rounded-lg p-8 text-center">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Got it! 👍</h3>
                  <p className="text-gray-600 mb-6">
                    {successMessage || "Thanks for reaching out! Our team will respond within a few hours."}
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false)
                      setSuccessMessage(null)
                      setError(null)
                      setFormTimestamp(Math.floor(Date.now() / 1000))
                      // Small delay to ensure state is updated before scrolling
                      setTimeout(() => {
                        scrollToForm()
                      }, 50)
                    }}
                    className="text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <div>
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                        <p className="text-red-700 text-sm">{error}</p>
                      </div>
                    </div>
                  )}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-2">
                      Inquiry Type
                    </label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="general">General Question</option>
                      <option value="support">Technical Support</option>
                      <option value="feature">Feature Request / Feedback</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>

                  {/* Honeypot Fields - Hidden from users */}
                  <div style={{ display: 'none' }} aria-hidden="true">
                    <label htmlFor="website">Website (do not fill)</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={honeypotFields.website}
                      onChange={(e) => setHoneypotFields({...honeypotFields, website: e.target.value})}
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <label htmlFor="url">URL (do not fill)</label>
                    <input
                      type="text"
                      id="url"
                      name="url"
                      value={honeypotFields.url}
                      onChange={(e) => setHoneypotFields({...honeypotFields, url: e.target.value})}
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <label htmlFor="phone_number">Phone (do not fill)</label>
                    <input
                      type="text"
                      id="phone_number"
                      name="phone_number"
                      value={honeypotFields.phone_number}
                      onChange={(e) => setHoneypotFields({...honeypotFields, phone_number: e.target.value})}
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <label htmlFor="fax">Fax (do not fill)</label>
                    <input
                      type="text"
                      id="fax"
                      name="fax"
                      value={honeypotFields.fax}
                      onChange={(e) => setHoneypotFields({...honeypotFields, fax: e.target.value})}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Quick answers to common questions
            </p>
          </motion.div>

          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Note */}
      <div className="py-16 bg-blue-600">
        <div className="mx-auto max-w-4xl text-center px-6">
          <h2 className="text-3xl font-bold text-white mb-4">Small Team = Real Conversations</h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            No support tickets. No corporate jargon. Just email us and you'll get a response
            from people who actually built this thing and use it every day. We're here to help! 🚀
          </p>
        </div>
      </div>
    </div>
  )
}

export default ContactPage